export type PermissionState = NotificationPermission | 'unsupported';

export interface NotifyContext {
  /** The "alert me when hidden" setting */
  enabled: boolean;
  /** document.hidden — alerts only make sense for a tab you are not looking at */
  hidden: boolean;
  permission: PermissionState;
  now: number;
  /** When this stream was last announced */
  lastAt?: number;
  minGapMs?: number;
}

/** One alert per stream per five minutes, however hard it keeps spiking */
export const NOTIFY_GAP_MS = 5 * 60 * 1000;

export const shouldNotify = (c: NotifyContext): boolean => {
  if (!c.enabled || !c.hidden || c.permission !== 'granted') return false;
  const gap = c.minGapMs ?? NOTIFY_GAP_MS;
  if (c.lastAt !== undefined && c.now - c.lastAt < gap) return false;
  return true;
};

export interface SpikeSubject {
  user_name: string;
  game_name: string;
  title: string;
}

export interface NotificationCopy {
  title: string;
  body: string;
}

export const spikeNotification = (s: SpikeSubject, heat: number, switched: boolean): NotificationCopy => {
  const title = `${s.user_name} · ${heat.toFixed(1)}× its usual chat`;
  const detail = [s.game_name, s.title].filter(Boolean).join(' · ');
  const lead = switched ? 'Switched' : 'Click to watch';
  return { title, body: detail ? `${lead} · ${detail}` : lead };
};

export const notificationPermission = (): PermissionState =>
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;

export const requestNotificationPermission = async (): Promise<PermissionState> => {
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
};

/** Fire the browser notification; clicking it brings the tab back and runs `onClick` */
export const showSpikeNotification = (copy: NotificationCopy, onClick: () => void): void => {
  try {
    const n = new Notification(copy.title, {
      body: copy.body,
      icon: '/pogchamp.png',
      tag: 'pogchamp-heat',
    });
    n.onclick = () => {
      window.focus();
      onClick();
      n.close();
    };
  } catch {
    // Notification constructors throw on some mobile browsers — alerts are best-effort
  }
};
